import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Dumbbell, Navigation, Loader2, Bookmark, ChevronDown, Trash2, ArrowRight } from 'lucide-react';
import { ALL_GYMS, GYM_STATES, STATE_NAMES } from '../../lib/hyroxGymData';
import { CITY_COORDS, getDistanceMiles } from '../../lib/cityCoords';
import { articlesApi } from '../../lib/api';
import { useGymReviewStats } from '../../components/GymReviewPanel';
import { getAuthToken } from '../../lib/tokenStorage';
import { GymCard } from './GymCard';
import { ComparePanel } from './ComparePanel';
import { CompareBar } from './CompareBar';
import { safeId } from './data';

export const GymFinder = ({ externalState, onExternalStateConsumed }) => {
  const [activeState, setActiveState] = useState('');
  const [activeCity, setActiveCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dirMatches, setDirMatches] = useState([]);
  const [filterTier, setFilterTier] = useState('all');
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showAllStates, setShowAllStates] = useState(false);

  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2) { setDirMatches([]); return undefined; }
    const t = setTimeout(() => {
      articlesApi.siteSearch(searchTerm.trim())
        .then(({ data }) => setDirMatches(data.gyms || []))
        .catch(() => setDirMatches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [geoStatus, setGeoStatus] = useState('idle');
  const [sortByDistance, setSortByDistance] = useState(false);

  const [savedComparisons, setSavedComparisons] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  const reviewStats = useGymReviewStats();

  const [gymContent, setGymContent] = useState({});
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gym-content`).then((r) => r.json()).then((data) => {
      const map = {};
      data.forEach((c) => { map[c.gym_key] = c; });
      setGymContent(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (externalState) {
      setActiveState(externalState);
      setActiveCity('');
      setShowAllStates(true);
      if (onExternalStateConsumed) onExternalStateConsumed();
    }
  }, [externalState, onExternalStateConsumed]);

  const citiesForState = useMemo(() => {
    if (!activeState) return [];
    return [...new Set(ALL_GYMS.filter((g) => g.state === activeState).map((g) => g.city))].sort();
  }, [activeState]);

  const stateCounts = useMemo(() => {
    const counts = {};
    ALL_GYMS.forEach((g) => { counts[g.state] = (counts[g.state] || 0) + 1; });
    return counts;
  }, []);

  const gymsWithDistance = useMemo(() => {
    if (!userLocation) return ALL_GYMS.map((g) => ({ ...g, distance: null }));
    return ALL_GYMS.map((g) => {
      const coords = CITY_COORDS[`${g.city}, ${g.state}`];
      if (!coords) return { ...g, distance: null };
      const dist = getDistanceMiles(userLocation.lat, userLocation.lng, coords[0], coords[1]);
      return { ...g, distance: Math.round(dist) };
    });
  }, [userLocation]);

  const handleGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      return;
    }
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setGeoStatus('success');
        setSortByDistance(true);

        let nearestState = '';
        let minDist = Infinity;
        GYM_STATES.forEach((st) => {
          ALL_GYMS.filter((g) => g.state === st).forEach((g) => {
            const coords = CITY_COORDS[`${g.city}, ${g.state}`];
            if (coords) {
              const d = getDistanceMiles(loc.lat, loc.lng, coords[0], coords[1]);
              if (d < minDist) { minDist = d; nearestState = st; }
            }
          });
        });
        if (nearestState) {
          setActiveState(nearestState);
          setActiveCity('');
        }
      },
      (err) => {
        if (err.code === 1) setGeoStatus('denied');
        else setGeoStatus('error');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const compareParam = params.get('compare');
    if (compareParam) {
      const slugPairs = decodeURIComponent(compareParam).split(',');
      const matched = slugPairs.map((pair) => {
        const [nameSlug, citySlug] = pair.split('__');
        return ALL_GYMS.find((g) => safeId(g.name) === nameSlug && safeId(g.city) === citySlug);
      }).filter(Boolean).slice(0, 3);
      if (matched.length >= 2) {
        setCompareList(matched);
        setShowCompare(true);
      }
    }
  }, []);

  const fetchSavedComparisons = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-comparisons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setSavedComparisons(await resp.json());
    } catch (err) { console.error('Fetch saved gym comparisons failed:', err); }
  }, []);

  useEffect(() => { fetchSavedComparisons(); }, [fetchSavedComparisons]);

  const loadSavedComparison = useCallback((comparison) => {
    const matched = comparison.gyms.map((g) =>
      ALL_GYMS.find((gym) => gym.name === g.name && gym.city === g.city && gym.state === g.state)
    ).filter(Boolean);
    if (matched.length >= 2) {
      setCompareList(matched);
      setShowCompare(true);
    }
    setShowSaved(false);
  }, []);

  const deleteSavedComparison = useCallback(async (compId) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-comparisons/${compId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      setSavedComparisons((prev) => prev.filter((c) => c.comparison_id !== compId));
    } catch (err) { console.error('Delete saved comparison failed:', err); }
  }, []);

  const toggleCompare = useCallback((gym) => {
    setCompareList((prev) => {
      const exists = prev.find((g) => g.name === gym.name && g.city === gym.city);
      if (exists) return prev.filter((g) => !(g.name === gym.name && g.city === gym.city));
      if (prev.length >= 3) return prev;
      return [...prev, gym];
    });
  }, []);

  const removeFromCompare = useCallback((gym) => {
    setCompareList((prev) => prev.filter((g) => !(g.name === gym.name && g.city === gym.city)));
  }, []);

  const filteredGyms = useMemo(() => {
    let results = gymsWithDistance;
    if (activeState) results = results.filter((g) => g.state === activeState);
    if (activeCity) results = results.filter((g) => g.city === activeCity);

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      results = results.filter((g) =>
        g.name.toLowerCase().includes(q) ||
        g.city.toLowerCase().includes(q) ||
        g.state.toLowerCase().includes(q) ||
        (STATE_NAMES[g.state] || '').toLowerCase().includes(q) ||
        g.tier.toLowerCase().includes(q) ||
        g.address.toLowerCase().includes(q) ||
        (g.notableInfo && g.notableInfo.toLowerCase().includes(q))
      );
    }

    if (filterTier === 'official') {
      results = results.filter((g) => g.tier.toLowerCase().includes('official') || g.tier.toLowerCase().includes('performance center'));
    } else if (filterTier === 'race-sims') {
      results = results.filter((g) => g.raceSims);
    }

    if (sortByDistance && userLocation) {
      results = [...results].sort((a, b) => {
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
    }

    return results;
  }, [gymsWithDistance, activeState, activeCity, searchTerm, filterTier, sortByDistance, userLocation]);

  const visibleStates = showAllStates ? GYM_STATES : GYM_STATES.slice(0, 12);

  const geoBtnClasses = (() => {
    if (geoStatus === 'success') return 'bg-[#e8ff00]/10 border border-[#e8ff00]/40 text-[#e8ff00]';
    if (geoStatus === 'loading') return 'bg-zinc-800 border border-zinc-700 text-zinc-400 cursor-wait';
    return 'bg-[#e8ff00] text-black hover:opacity-90';
  })();

  const geoBtnLabel = (() => {
    if (geoStatus === 'loading') return <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Locating...</>;
    if (geoStatus === 'success') return <><Navigation className="w-3.5 h-3.5" /> Sorted by Distance</>;
    return <><Navigation className="w-3.5 h-3.5" /> Find Gyms Near Me</>;
  })();

  return (
    <div data-testid="gym-finder-component">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 bg-[#e8ff00]/5 border border-[#e8ff00]/20 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Dumbbell className="w-4 h-4 text-[#e8ff00] flex-shrink-0" />
          <p className="text-zinc-300 text-sm">
            <span className="text-white font-heading">{ALL_GYMS.length}</span> HYROX gyms across <span className="text-[#e8ff00] font-heading">{GYM_STATES.length} states</span>
          </p>
        </div>
        <button
          onClick={handleGeolocation}
          disabled={geoStatus === 'loading'}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-heading uppercase tracking-wider transition-all flex-shrink-0 ${geoBtnClasses}`}
          data-testid="gym-near-me-btn"
        >
          {geoBtnLabel}
        </button>
      </div>

      {geoStatus === 'denied' && (
        <div className="mb-4 px-4 py-2.5 bg-red-500/5 border border-red-500/20 text-red-400 text-xs" data-testid="geo-denied-msg">
          Location access was denied. You can still browse gyms by state and city below.
        </div>
      )}
      {geoStatus === 'error' && (
        <div className="mb-4 px-4 py-2.5 bg-red-500/5 border border-red-500/20 text-red-400 text-xs" data-testid="geo-error-msg">
          Unable to determine your location. Please try again or browse by state below.
        </div>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search all HYROX gyms by name, city, or state..."
          className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
          data-testid="gym-search-input"
        />
      </div>

      {dirMatches.length > 0 && (
        <div className="mb-5 border border-[#e8ff00]/30 bg-[#e8ff00]/5 p-4" data-testid="dir-search-results">
          <p className="text-[#e8ff00] text-[10px] font-heading uppercase tracking-widest mb-2.5">
            Also found in our City Gym Directories — full profiles, pricing &amp; schedules
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {dirMatches.map((g) => (
              <Link
                key={g.path}
                to={g.path}
                className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-[#e8ff00]/50 px-4 py-3 transition-colors"
                data-testid={`dir-match-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">{g.title}</p>
                  <p className="text-zinc-500 text-xs truncate">{g.context}</p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-[#e8ff00] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="mb-2" />

      <div className="mb-4">
        <div className="flex flex-wrap gap-1.5 mb-2">
          <button
            onClick={() => { setActiveState(''); setActiveCity(''); }}
            className={`px-3 py-1.5 text-[11px] font-heading uppercase tracking-wider transition-all ${
              !activeState ? 'bg-[#e8ff00] text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600'
            }`}
            data-testid="gym-state-all"
          >
            All States
          </button>
          {visibleStates.map((st) => (
            <button
              key={st}
              onClick={() => { setActiveState(activeState === st ? '' : st); setActiveCity(''); }}
              className={`px-3 py-1.5 text-[11px] font-heading uppercase tracking-wider transition-all ${
                activeState === st ? 'bg-[#e8ff00] text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600'
              }`}
              data-testid={`gym-state-${st.toLowerCase()}`}
            >
              {st} <span className="opacity-60">({stateCounts[st] || 0})</span>
            </button>
          ))}
          {!showAllStates && GYM_STATES.length > 12 && (
            <button
              onClick={() => setShowAllStates(true)}
              className="px-3 py-1.5 text-[11px] font-heading uppercase tracking-wider bg-zinc-900 border border-zinc-800 text-[#e8ff00] hover:bg-[#e8ff00]/10 transition-all"
              data-testid="gym-show-all-states"
            >
              +{GYM_STATES.length - 12} more
            </button>
          )}
        </div>
      </div>

      {activeState && citiesForState.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button
            onClick={() => setActiveCity('')}
            className={`px-3 py-1.5 text-[11px] font-heading uppercase tracking-wider transition-all ${
              !activeCity ? 'bg-zinc-700 text-white' : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white'
            }`}
            data-testid="gym-city-all"
          >
            All Cities
          </button>
          {citiesForState.map((city) => {
            const cityCount = ALL_GYMS.filter((g) => g.state === activeState && g.city === city).length;
            return (
              <button
                key={city}
                onClick={() => setActiveCity(activeCity === city ? '' : city)}
                className={`px-3 py-1.5 text-[11px] font-heading uppercase tracking-wider transition-all ${
                  activeCity === city ? 'bg-zinc-700 text-white' : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white'
                }`}
                data-testid={`gym-city-${city.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {city} ({cityCount})
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          {[
            { v: 'all', l: 'All Types' },
            { v: 'official', l: 'Official Only' },
            { v: 'race-sims', l: 'Race Sims' },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilterTier(f.v)}
              className={`px-3 py-2 text-xs font-heading uppercase tracking-wider border transition-colors whitespace-nowrap ${
                filterTier === f.v ? 'text-[#e8ff00] border-[#e8ff00]/40 bg-[#e8ff00]/5' : 'text-zinc-500 border-zinc-700 hover:text-white'
              }`}
              data-testid={`gym-filter-${f.v}`}
            >
              {f.l}
            </button>
          ))}
          {userLocation && (
            <button
              onClick={() => setSortByDistance((v) => !v)}
              className={`px-3 py-2 text-xs font-heading uppercase tracking-wider border transition-colors whitespace-nowrap ${
                sortByDistance ? 'text-[#e8ff00] border-[#e8ff00]/40 bg-[#e8ff00]/5' : 'text-zinc-500 border-zinc-700 hover:text-white'
              }`}
              data-testid="gym-sort-distance"
            >
              <span className="flex items-center gap-1.5"><Navigation className="w-3 h-3" /> Nearest First</span>
            </button>
          )}
        </div>
        {compareList.length > 0 && (
          <p className="text-[#e8ff00] text-xs font-heading">{compareList.length} gym{compareList.length !== 1 ? 's' : ''} selected to compare</p>
        )}
      </div>

      {savedComparisons.length > 0 && (
        <div className="relative mb-4" data-testid="saved-comparisons-section">
          <button
            onClick={() => setShowSaved((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 text-xs font-heading uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-[#e8ff00] hover:border-[#e8ff00]/40 transition-all"
            data-testid="saved-comparisons-toggle"
          >
            <Bookmark className="w-3.5 h-3.5" />
            Saved Comparisons ({savedComparisons.length})
            <ChevronDown className={`w-3 h-3 transition-transform ${showSaved ? 'rotate-180' : ''}`} />
          </button>
          {showSaved && (
            <div className="absolute z-30 mt-1 w-full max-w-md bg-[#0a0a0a] border border-zinc-800 shadow-xl" data-testid="saved-comparisons-dropdown">
              {savedComparisons.map((c) => (
                <div key={c.comparison_id} className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/60 last:border-0 hover:bg-zinc-900/80 transition-colors">
                  <button
                    onClick={() => loadSavedComparison(c)}
                    className="flex-1 text-left"
                    data-testid={`load-comparison-${c.comparison_id}`}
                  >
                    <p className="text-white text-xs font-heading truncate">{c.name}</p>
                    <p className="text-zinc-500 text-[10px] mt-0.5">{c.gyms.map((g) => g.name).join(' vs ')}</p>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSavedComparison(c.comparison_id); }}
                    className="text-zinc-600 hover:text-red-400 transition-colors p-1 ml-2"
                    data-testid={`delete-comparison-${c.comparison_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <p className="text-zinc-500 text-xs">
          Showing <span className="text-white font-heading">{filteredGyms.length}</span> gym{filteredGyms.length !== 1 ? 's' : ''}
          {activeState && <> in <span className="text-[#e8ff00]">{STATE_NAMES[activeState] || activeState}</span></>}
          {activeCity && <>, <span className="text-white">{activeCity}</span></>}
          {sortByDistance && userLocation && <> &mdash; <span className="text-[#e8ff00]">sorted by distance</span></>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gym-cards-grid">
        {filteredGyms.slice(0, 50).map((gym) => (
          <GymCard
            key={`${gym.name}-${gym.city}-${gym.state}`}
            gym={gym}
            isSelected={compareList.some((g) => g.name === gym.name && g.city === gym.city)}
            onToggleCompare={toggleCompare}
            compareCount={compareList.length}
            distance={gym.distance}
            reviewStats={reviewStats}
            content={gymContent[`${gym.name}|${gym.city}|${gym.state}`]}
          />
        ))}
      </div>

      {filteredGyms.length > 50 && (
        <p className="text-center text-zinc-500 text-xs mt-4 py-3 bg-zinc-900/50 border border-zinc-800">
          Showing 50 of {filteredGyms.length} results. Narrow your search by selecting a state or city above.
        </p>
      )}

      {filteredGyms.length === 0 && (
        <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800">
          <MapPin className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No gyms found matching your search.</p>
          <button
            onClick={() => { setSearchTerm(''); setFilterTier('all'); setActiveState(''); setActiveCity(''); }}
            className="text-[#e8ff00] text-xs font-heading uppercase tracking-wider mt-2 hover:text-white transition-colors"
            data-testid="gym-clear-filters"
          >
            Clear Filters
          </button>
        </div>
      )}

      <CompareBar
        selected={compareList}
        onRemove={removeFromCompare}
        onCompare={() => setShowCompare(true)}
        onClear={() => setCompareList([])}
      />

      {showCompare && (
        <ComparePanel
          gyms={compareList}
          onRemove={removeFromCompare}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
};
